import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type InputProps = React.ComponentProps<"input">;

export interface PasswordFieldProps extends Omit<InputProps, "type"> {
  containerClassName?: string;
  capsLockWarningClassName?: string;
}

export const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(
    { className, containerClassName, capsLockWarningClassName, onKeyDown, onKeyUp, ...rest },
    ref,
  ) {
    const { t } = useTranslation("auth");
    const [visible, setVisible] = React.useState(false);
    const [capsLockOn, setCapsLockOn] = React.useState(false);

    const handleKey = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (typeof event.getModifierState === "function") {
          setCapsLockOn(event.getModifierState("CapsLock"));
        }
      },
      [],
    );

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      handleKey(event);
      onKeyDown?.(event);
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
      handleKey(event);
      onKeyUp?.(event);
    };

    const toggle = () => setVisible((v) => !v);
    const Icon = visible ? EyeOff : Eye;
    const ariaLabel = visible ? t("passwordField.hideAria") : t("passwordField.showAria");

    return (
      <div className={cn("flex flex-col gap-1", containerClassName)}>
        <div className="relative">
          <Input
            ref={ref}
            type={visible ? "text" : "password"}
            className={cn("pr-10", className)}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            {...rest}
          />
          <button
            type="button"
            onClick={toggle}
            aria-label={ariaLabel}
            aria-pressed={visible}
            tabIndex={-1}
            className={cn(
              "absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none",
              rest.disabled && "pointer-events-none opacity-50",
            )}
          >
            <Icon
              className="size-4"
              aria-hidden="true"
              fill={visible ? "currentColor" : "none"}
            />
          </button>
        </div>
        {capsLockOn ? (
          <p
            role="alert"
            className={cn(
              "text-xs text-status-pending [color:var(--color-status-pending)]",
              capsLockWarningClassName,
            )}
          >
            {t("passwordField.capsLockWarning")}
          </p>
        ) : null}
      </div>
    );
  },
);
